import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { CalendarClock, LogIn, Clock, Coffee, ArrowRight, ListOrdered } from "lucide-react";
import DashboardWrapper from "../../components/Layout/DashboardWrapper";
import { getUserData, setStoragePatient } from "../../utils/storageUtils";
import { isDoctor } from "../../constants/roles";
import { setCurrentPatient } from "../../redux/users/actions";
import { getUserById } from "../../services/users";
import { getDoctorQueue, takeCharge } from "../../services/queue";
import { QueueEntryApi, hm, initials } from "../../services/queue/types";
import useQueueStream from "../../hooks/useQueueStream";

const nowHm = () =>
  new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const QueueMedecin: React.FC = () => {
  const userData = getUserData();
  const allowed = isDoctor(userData);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [entries, setEntries] = useState<QueueEntryApi[]>([]);
  const [clock, setClock] = useState(nowHm());

  useEffect(() => {
    const handle = window.setInterval(() => setClock(nowHm()), 30000);
    return () => window.clearInterval(handle);
  }, []);

  const doctorQuery = useQuery(["queue-doctor"], () => getDoctorQueue(), {
    enabled: allowed,
    refetchInterval: 60000, // filet de sécurité si le flux SSE tombe
    onSuccess: (res: any) => {
      if (res?.ok) setEntries((res.data ?? []) as QueueEntryApi[]);
    },
  });

  // Temps réel : le serveur pousse la file FIFO à jour à chaque changement.
  useQueueStream((queue) => setEntries(queue), allowed);

  const takeChargeMut = useMutation((entry: QueueEntryApi) => takeCharge(entry.id), {
    onSuccess: async (res: any, entry: QueueEntryApi) => {
      if (!res?.ok) return;
      const patientId = res.data?.patientId ?? entry.patientId;
      // Charge la fiche patient COMPLÈTE (âge, antécédents, titre…) avant de
      // naviguer : l'entrée de file ne porte que id + nom/prénom, ce qui
      // laissait la page consultation incomplète jusqu'au rafraîchissement.
      const full: any = await getUserById(patientId);
      const patient =
        full?.ok && full.data
          ? full.data
          : { id: patientId, firstName: entry.patientFirstName, lastName: entry.patientLastName };
      dispatch(setCurrentPatient(patient));
      setStoragePatient(patient);
      queryClient.invalidateQueries(["queue-doctor"]);
      navigate("/consultation");
    },
  });

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return (
    <DashboardWrapper showSpinner={doctorQuery.isLoading && entries.length === 0}>
      <div className="font-poppins max-w-[1080px] mx-auto p-6 space-y-6">
        {/* En-tête : titre + temps réel + horloge */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Ma liste d'attente</h1>
          <div className="flex items-center gap-4">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/10 text-xs font-medium"
              style={{ color: "hsl(var(--success))" }}
            >
              <span className="relative flex w-2 h-2">
                <span
                  className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping"
                  style={{ background: "hsl(var(--success))" }}
                />
                <span
                  className="relative inline-flex w-2 h-2 rounded-full"
                  style={{ background: "hsl(var(--success))" }}
                />
              </span>
              Temps réel
            </span>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary text-secondary-foreground text-sm font-medium">
              <Clock className="w-4 h-4" />
              <span>{clock}</span>
            </div>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-card py-20 flex flex-col items-center text-center">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-secondary mb-4">
              <Coffee className="w-7 h-7 text-muted-foreground" />
            </div>
            <p className="text-lg font-semibold">Aucun patient en attente</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">
              Les patients que la secrétaire vous assigne apparaîtront ici automatiquement, dans l'ordre d'arrivée.
            </p>
          </div>
        ) : (
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <ListOrdered className="w-4 h-4" /> Mes patients présents — ordre d'arrivée
              </h2>
              <span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold">
                {entries.length} en attente
              </span>
            </div>

            <div className="space-y-2.5">
              {entries.map((entry, index) => {
                const next = index === 0;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    disabled={takeChargeMut.isLoading}
                    onClick={() => takeChargeMut.mutate(entry)}
                    title="Prendre en charge — ouvre la page du patient"
                    className={
                      (next
                        ? "border-2 bg-card "
                        : "border border-border bg-card ") +
                      "w-full text-left rounded-lg p-4 flex items-center justify-between transition-colors hover:bg-secondary/40 disabled:opacity-50"
                    }
                    style={next ? { borderColor: "hsl(var(--primary))", background: "hsl(var(--primary)/0.04)" } : undefined}
                  >
                    <div className="flex items-center gap-4">
                      {next ? (
                        <div className="flex flex-col items-center justify-center w-9">
                          <span className="text-lg font-bold" style={{ color: "hsl(var(--primary))" }}>
                            1
                          </span>
                          <span
                            className="text-[10px] uppercase font-semibold tracking-wide"
                            style={{ color: "hsl(var(--primary))" }}
                          >
                            Suivant
                          </span>
                        </div>
                      ) : (
                        <span className="w-9 text-center text-lg font-bold text-muted-foreground">
                          {index + 1}
                        </span>
                      )}
                      <span className="flex items-center justify-center w-11 h-11 rounded-full bg-secondary font-semibold">
                        {initials(entry.patientFirstName, entry.patientLastName)}
                      </span>
                      <div>
                        <p className="font-semibold">
                          {entry.patientFirstName} <span className="uppercase">{entry.patientLastName}</span>
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-sm flex-wrap">
                          {entry.walkIn ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-secondary text-secondary-foreground font-medium text-xs">
                              Sans RDV
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-medium text-xs"
                              style={{ background: "hsl(var(--primary)/0.08)", color: "hsl(var(--primary))" }}
                            >
                              <CalendarClock className="w-3 h-3" /> RDV {hm(entry.appointmentTime)}
                            </span>
                          )}
                          {entry.arrivalTime && (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <LogIn className="w-3.5 h-3.5" /> Arrivé {hm(entry.arrivalTime)}
                            </span>
                          )}
                          {entry.waitingMinutes != null && (
                            <>
                              <span className="text-muted-foreground/40">·</span>
                              <span className="text-muted-foreground text-xs">{entry.waitingMinutes} min d'attente</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <span
                      className={
                        next
                          ? "inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium"
                          : "inline-flex items-center gap-2 h-10 px-5 rounded-md border border-border font-medium"
                      }
                    >
                      Prendre en charge <ArrowRight className="w-4 h-4" />
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
              <ListOrdered className="w-3.5 h-3.5" />
              Ordre d'arrivée (FIFO). Cliquer un patient ouvre sa page et le retire de la liste.
            </p>
          </section>
        )}
      </div>
    </DashboardWrapper>
  );
};

export default QueueMedecin;
